// TanStack Query hooks for the clips endpoints (create from a recording, list, delete,
// retry). Lists poll while any clip is still being built by the worker.

import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { apiFetch } from "./client";
import type { ClipCreateRequest, ClipDeleteResponse, ClipList, ClipRead } from "./types";

const IN_FLIGHT = new Set(["queued", "processing"]);

function pollWhileBuilding(data: ClipList | undefined): number | false {
  return data?.items.some((c) => IN_FLIGHT.has(c.status)) ? 3000 : false;
}

export interface ClipsFilter {
  status?: string; // comma-separated ClipStatus values
  limit?: number;
  offset?: number;
}

export function useClips(filter: ClipsFilter = {}) {
  const p = new URLSearchParams();
  if (filter.status) p.set("status", filter.status);
  if (filter.limit != null) p.set("limit", String(filter.limit));
  if (filter.offset != null) p.set("offset", String(filter.offset));
  const qs = p.toString();
  return useQuery({
    queryKey: ["clips", filter],
    queryFn: () => apiFetch<ClipList>(`/api/clips${qs ? `?${qs}` : ""}`),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => pollWhileBuilding(query.state.data),
  });
}

/** One clip (the preview page). Polls while the worker is still building it. */
export function useClip(id: number | string) {
  return useQuery({
    queryKey: ["clip", String(id)],
    queryFn: () => apiFetch<ClipRead>(`/api/clips/${id}`),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && IN_FLIGHT.has(status) ? 3000 : false;
    },
  });
}

/** All clips of one recording (the detail-page card). */
export function useRecordingClips(recordingId: number | string) {
  return useQuery({
    queryKey: ["recording-clips", String(recordingId)],
    queryFn: () => apiFetch<ClipList>(`/api/recordings/${recordingId}/clips`),
    refetchInterval: (query) => pollWhileBuilding(query.state.data),
  });
}

export function useCreateClips(recordingId: number | string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ClipCreateRequest) =>
      apiFetch<ClipList>(`/api/recordings/${recordingId}/clips`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recording-clips", String(recordingId)] });
      qc.invalidateQueries({ queryKey: ["clips"] });
      // accepted AI suggestions flip to `accepted` server-side
      qc.invalidateQueries({ queryKey: ["clip-suggestions", String(recordingId)] });
      qc.invalidateQueries({ queryKey: ["recording", String(recordingId)] });
    },
  });
}

export function useDeleteClip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clipId: number) =>
      apiFetch<ClipDeleteResponse>(`/api/clips/${clipId}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clips"] });
      qc.invalidateQueries({ queryKey: ["recording-clips"] });
      // a clip accepted from an AI suggestion hands its window back to the suggestion
      qc.invalidateQueries({ queryKey: ["clip-suggestions"] });
    },
  });
}

export function useRetryClip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clipId: number) =>
      apiFetch<ClipRead>(`/api/clips/${clipId}/retry`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clips"] });
      qc.invalidateQueries({ queryKey: ["recording-clips"] });
    },
  });
}
